import * as React from 'react';
import Box from '@mui/material/Box';
import Grid from '@mui/material/Grid';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import CardActionArea from '@mui/material/CardActionArea';
import Chip from '@mui/material/Chip';
import Typography from '@mui/material/Typography';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import { useNavigate } from 'react-router-dom';
import { getDescriptions } from "../api/algorithm-description";
import { AlgorithmDescription } from "../types/dbTypes";

const extractVersion = (title: string) => {
  const match = title.match(/(?:Versi[oó]n\s+|^)(20\d{2})/i);
  return match ? Number(match[1]) : 0;
};

const AvailableAlgorithmList: React.FC = () => {
  const [algorithms, setAlgorithms] = React.useState<
    AlgorithmDescription[] | null
  >(null);

  React.useEffect(() => {
    if (algorithms == null) {
      getDescriptions().then((res) => setAlgorithms(res));
    }
  });

  const navigate = useNavigate();
  const sortedAlgorithms = React.useMemo(() => {
    if (!algorithms) return [];

    return [...algorithms].sort((a, b) => {
      const versionDiff = extractVersion(b.title) - extractVersion(a.title);

      if (versionDiff !== 0) return versionDiff;

      return a.title.localeCompare(b.title);
    });
  }, [algorithms]);

  const latestVersion = React.useMemo(
    () => sortedAlgorithms.reduce((max, algorithm) => Math.max(max, extractVersion(algorithm.title)), 0),
    [sortedAlgorithms],
  );

  return (
    <Grid container spacing={3}>
      {sortedAlgorithms.map((algorithm) => {
        const version = extractVersion(algorithm.title);
        const isLatest = version > 0 && version === latestVersion;

        return (
          <Grid item xs={12} md={6} key={algorithm.id}>
            <Card
              variant="outlined"
              sx={{
                height: '100%',
                position: 'relative',
                transition: 'transform 0.2s, box-shadow 0.2s',
                borderColor: isLatest ? 'secondary.main' : 'divider',
                boxShadow: isLatest ? (theme) => `0 10px 30px ${theme.palette.secondary.main}22` : undefined,
                '&:hover': {
                  transform: 'translateY(-4px)',
                  boxShadow: (theme) => `0 4px 20px ${theme.palette.action.hover}`,
                },
              }}
            >
              <CardActionArea
                onClick={() => navigate(`/assistant/${algorithm.id}`)}
                sx={{ height: '100%' }}
              >
                <CardContent sx={{ height: '100%', p: 3 }}>
                  {isLatest && (
                    <Chip
                      label="Mas reciente"
                      color="secondary"
                      size="small"
                      sx={{ mb: 2, fontWeight: 700 }}
                    />
                  )}
                  <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2, mb: 2 }}>
                    <PlayArrowIcon
                      sx={{
                        fontSize: 40,
                        color: isLatest ? 'secondary.dark' : 'primary.main',
                        bgcolor: isLatest ? 'secondary.light' : 'action.hover',
                        borderRadius: '50%',
                        p: 1,
                      }}
                    />
                    <Box sx={{ flex: 1 }}>
                      <Typography variant="h6" gutterBottom>
                        {algorithm.title}
                      </Typography>
                    </Box>
                  </Box>
                </CardContent>
              </CardActionArea>
            </Card>
          </Grid>
        );
      })}
    </Grid>
  );
};

export default AvailableAlgorithmList;
